// src/utils/citationFormat.js
//
// Dependency-free citation formatting for APA, MLA, and Chicago (author-date).
// `formatInText(src, style, locator)` returns a short plain-text citation for
// insertion at the caret (locator = optional page/section, e.g. "12" or "12-14").
// `formatBibliographyEntry` returns an HTML string (with <em> for titles) for a
// works-cited / references list. The shapes match the source fields persisted by
// backend/routes/citations.py. This is intentionally pragmatic — swap in a full
// library (e.g. citation-js) later without changing call sites.
//
// Source types handled: book, journal, website, other (→ book-like).

const STYLES = ['apa', 'mla', 'chicago'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_ABBR = [
  'Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'June',
  'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
];

// Format an ISO-ish date (YYYY-MM-DD) into a human date. `abbr` gives MLA's
// "4 July 2026"; otherwise APA/Chicago's "July 4, 2026". Returns '' if unparsable.
const formatDate = (raw, { abbr = false } = {}) => {
  if (!raw) return '';
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(raw).trim());
  if (!m) return String(raw).trim(); // already human-typed — pass through
  const [, y, mo, d] = m;
  const mi = Math.min(11, Math.max(0, parseInt(mo, 10) - 1));
  const day = parseInt(d, 10);
  return abbr ? `${day} ${MONTHS_ABBR[mi]} ${y}` : `${MONTHS[mi]} ${day}, ${y}`;
};

// "p. 12" vs "pp. 12-14" based on whether the locator spans a range/list.
const pagePrefix = (loc) => (/[-–,]/.test(String(loc)) ? 'pp.' : 'p.');

// Parse "Last, First M." or "First M. Last" into { last, first }.
const parseAuthor = (raw) => {
  const name = (raw || '').trim();
  if (!name) return { last: '', first: '' };
  if (name.includes(',')) {
    const [last, first] = name.split(',');
    return { last: last.trim(), first: (first || '').trim() };
  }
  const parts = name.split(/\s+/);
  const last = parts.pop();
  return { last, first: parts.join(' ') };
};

const initials = (first) =>
  (first || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => `${p[0].toUpperCase()}.`)
    .join(' ');

const authorList = (src) => (Array.isArray(src.authors) ? src.authors.filter(Boolean) : []);

// Family name of the first author, for short in-text citations.
const firstAuthorLast = (src) => {
  const list = authorList(src);
  if (!list.length) return src.title || 'Anon.';
  return parseAuthor(list[0]).last;
};

const kind = (src) => {
  if (src.type === 'website') return 'website';
  if (src.type === 'journal' || !!src.journal) return 'journal';
  return 'book';
};

// ── In-text ────────────────────────────────────────────────────────────────

export const formatInText = (src, style = 'apa', locator = '') => {
  const list = authorList(src);
  const year = src.year || 'n.d.';
  const st = STYLES.includes(style) ? style : 'apa';
  const loc = (locator || '').trim();

  const authorsShort = () => {
    if (!list.length) return src.title || 'Anon.';
    const lasts = list.map((a) => parseAuthor(a).last);
    if (lasts.length === 1) return lasts[0];
    if (lasts.length === 2) return st === 'apa' ? `${lasts[0]} & ${lasts[1]}` : `${lasts[0]} and ${lasts[1]}`;
    return `${lasts[0]} et al.`;
  };

  if (st === 'mla') {
    // (Author page) — locator overrides the source's stored pages.
    const page = loc || src.pages || '';
    const who = firstAuthorLast(src);
    return page ? `(${who} ${page})` : `(${who})`;
  }
  if (st === 'chicago') {
    return loc ? `(${authorsShort()} ${year}, ${loc})` : `(${authorsShort()} ${year})`;
  }
  // apa
  const tail = loc ? `, ${pagePrefix(loc)} ${loc}` : '';
  return `(${authorsShort()}, ${year}${tail})`;
};

// ── Bibliography ─────────────────────────────────────────────────────────────

const em = (s) => (s ? `<em>${s}</em>` : '');

const apaAuthors = (list) => {
  if (!list.length) return '';
  const formatted = list.map((a) => {
    const { last, first } = parseAuthor(a);
    return first ? `${last}, ${initials(first)}` : last;
  });
  if (formatted.length === 1) return formatted[0];
  return `${formatted.slice(0, -1).join(', ')}, & ${formatted[formatted.length - 1]}`;
};

const mlaAuthors = (list) => {
  if (!list.length) return '';
  const first = parseAuthor(list[0]);
  const head = first.first ? `${first.last}, ${first.first}` : first.last;
  if (list.length === 1) return head;
  const rest = list.slice(1).map((a) => {
    const { last, first: f } = parseAuthor(a);
    return f ? `${f} ${last}` : last;
  });
  return `${head}, and ${rest.join(', and ')}`;
};

// Trailing DOI/URL, preferring a DOI (as a doi.org link) when present.
const linkTail = (src) => {
  if (src.doi) return `https://doi.org/${src.doi.replace(/^https?:\/\/doi\.org\//, '')}`;
  if (src.url) return src.url;
  return '';
};

export const formatBibliographyEntry = (src, style = 'apa') => {
  const st = STYLES.includes(style) ? style : 'apa';
  const list = authorList(src);
  const year = src.year || 'n.d.';
  const k = kind(src);
  const site = src.containerTitle || src.publisher || '';

  // ── MLA ──
  if (st === 'mla') {
    const parts = [];
    if (list.length) parts.push(`${mlaAuthors(list)}.`);
    if (k === 'journal') {
      parts.push(`“${src.title}.”`);
      let j = em(src.journal);
      if (src.volume) j += `, vol. ${src.volume}`;
      if (src.issue) j += `, no. ${src.issue}`;
      if (src.year) j += `, ${src.year}`;
      if (src.pages) j += `, pp. ${src.pages}`;
      parts.push(`${j}.`);
      const link = linkTail(src);
      if (link) parts.push(`${link}.`);
    } else if (k === 'website') {
      parts.push(`“${src.title}.”`);
      let s = em(site);
      if (src.year) s += `${site ? ', ' : ''}${src.year}`;
      if (s) parts.push(`${s}.`);
      if (src.url) parts.push(`${src.url}.`);
      const acc = formatDate(src.accessed, { abbr: true });
      if (acc) parts.push(`Accessed ${acc}.`);
    } else {
      parts.push(`${em(src.title)}.`);
      const pub = [src.publisher, src.year].filter(Boolean).join(', ');
      if (pub) parts.push(`${pub}.`);
      const link = linkTail(src);
      if (link) parts.push(`${link}.`);
    }
    return parts.join(' ').trim();
  }

  // ── Chicago (author-date) ──
  if (st === 'chicago') {
    const parts = [];
    if (list.length) parts.push(`${mlaAuthors(list)}.`);
    parts.push(`${year}.`);
    if (k === 'journal') {
      parts.push(`“${src.title}.”`);
      let j = em(src.journal);
      if (src.volume) j += ` ${src.volume}`;
      if (src.issue) j += `, no. ${src.issue}`;
      if (src.pages) j += `: ${src.pages}`;
      parts.push(`${j}.`);
      const link = linkTail(src);
      if (link) parts.push(`${link}.`);
    } else if (k === 'website') {
      parts.push(`“${src.title}.”`);
      if (site) parts.push(`${site}.`);
      const acc = formatDate(src.accessed);
      if (acc) parts.push(`Accessed ${acc}.`);
      if (src.url) parts.push(`${src.url}.`);
    } else {
      parts.push(`${em(src.title)}.`);
      const place = [src.publisherPlace, src.publisher].filter(Boolean).join(': ');
      if (place) parts.push(`${place}.`);
      const link = linkTail(src);
      if (link) parts.push(`${link}.`);
    }
    return parts.join(' ').trim();
  }

  // ── APA ──
  const parts = [];
  const auth = apaAuthors(list);
  if (auth) parts.push(`${auth}`);
  parts.push(`(${year}).`);
  if (k === 'journal') {
    parts.push(`${src.title}.`);
    let j = em(src.journal);
    if (src.volume) j += `, ${em(src.volume)}`;
    if (src.issue) j += `(${src.issue})`;
    if (src.pages) j += `, ${src.pages}`;
    parts.push(`${j}.`);
    const link = linkTail(src);
    if (link) parts.push(link);
  } else if (k === 'website') {
    parts.push(`${em(src.title)}.`);
    if (site) parts.push(`${site}.`);
    const acc = formatDate(src.accessed);
    if (acc && src.url) parts.push(`Retrieved ${acc}, from ${src.url}`);
    else if (src.url) parts.push(src.url);
  } else {
    parts.push(`${em(src.title)}.`);
    if (src.publisher) parts.push(`${src.publisher}.`);
    const link = linkTail(src);
    if (link) parts.push(link);
  }
  return parts.join(' ').trim();
};

export const CITATION_STYLES = STYLES;
